import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { ConfigurationSection } from "../StyleComponents";
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { Switch } from '@shared/ui/atoms/Switch';
import {
  getWhereaboutsReminderConfig,
  updateWhereaboutsReminderConfig,
  WhereaboutsReminderConfig
} from '@features/settings/services/CompanyService';
import { useTranslation } from "react-i18next";
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import { TIMEZONES } from '@shared/utils/Timezones';
import styled from "styled-components";
import moment from "moment";

function getDaysOfWeek() {
  return [
    {id: 1, name: moment().day(1).format('dddd')},
    {id: 2, name: moment().day(2).format('dddd')},
    {id: 3, name: moment().day(3).format('dddd')},
    {id: 4, name: moment().day(4).format('dddd')},
    {id: 5, name: moment().day(5).format('dddd')},
    {id: 6, name: moment().day(6).format('dddd')},
    {id: 7, name: moment().day(7).format('dddd')},
  ]
}

const HOUR_TO_SEND_REMINDER = [
  {id: 0, name: '00:00'},
  {id: 1, name: '01:00'},
  {id: 2, name: '02:00'},
  {id: 3, name: '03:00'},
  {id: 4, name: '04:00'},
  {id: 5, name: '05:00'},
  {id: 6, name: '06:00'},
  {id: 7, name: '07:00'},
  {id: 8, name: '08:00'},
  {id: 9, name: '09:00'},
  {id: 10, name: '10:00'},
  {id: 11, name: '11:00'},
  {id: 12, name: '12:00'},
  {id: 13, name: '13:00'},
  {id: 14, name: '14:00'},
  {id: 15, name: '15:00'},
  {id: 16, name: '16:00'},
  {id: 17, name: '17:00'},
  {id: 18, name: '18:00'},
  {id: 19, name: '19:00'},
  {id: 20, name: '20:00'},
  {id: 21, name: '21:00'},
  {id: 22, name: '22:00'},
  {id: 23, name: '23:00'},
]
const timezones = TIMEZONES;

export const WhereaboutsReminderSetting = forwardRef<WhereaboutsReminderSettingHandle, Props>((props, ref) => {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState<undefined | WhereaboutsReminderConfig>()

  useEffect(() => {
    getWhereaboutsReminderConfig()
      .then((result: WhereaboutsReminderConfig) => {
        setConfig(result);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const applyChange = async (updatedConfig: WhereaboutsReminderConfig) => {
    if (props.deferSave) {
      setConfig(updatedConfig);
      return;
    }
    setIsLoading(true);
    try {
      await updateWhereaboutsReminderConfig(updatedConfig);
      setConfig(updatedConfig);
    } finally {
      setIsLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!config) {
        return;
      }
      setIsLoading(true);
      try {
        await updateWhereaboutsReminderConfig(config);
      } finally {
        setIsLoading(false);
      }
    },
  }), [config]);

  const onEnableChange = async (result: boolean) => {
    if (!config) return;
    await applyChange({...config, enabled: result});
  }

  const onDayOfWeekSelected = async (result: any) => {
    if (!config) return;
    await applyChange({...config, dayOfWeek: result});
  }

  const onHourToSendSelected = async (result: any) => {
    if (!config) return;
    await applyChange({...config, hourOfDay: parseInt(result)});
  }

  const onTimezoneSelected = async (result: any) => {
    const timezone = timezones.find(tz => tz.id === result);
    if (!timezone || !config) return;
    await applyChange({...config, timezone: timezone.id});
  }

  return (
    <ConfigurationSection>
      <BodyLarge colour={Colours.blue}>{t('settings.whereabouts-reminder-config.title')}</BodyLarge>
      <BodyRegular>{t('settings.whereabouts-reminder-config.subtitle')}</BodyRegular>
      {config && <>
        <SpaceBetweenRow>
          <BodyRegular weight={600}>{t('settings.whereabouts-reminder-config.enable')}</BodyRegular>
          <Switch value={!!config?.enabled} label="" disabled={isLoading} onChange={onEnableChange}/>
          {isLoading && <SimpleSpinner size={32} />}
        </SpaceBetweenRow>

        <SpaceBetweenRow>
          <BodyRegular style={{whiteSpace: 'nowrap', marginRight: 16}}
                       weight={600}>{t('settings.whereabouts-reminder-config.day')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={isLoading}
                               onSelect={onDayOfWeekSelected}
                               items={getDaysOfWeek()}
                               placeholder={'settings.whereabouts-reminder-config.day-placeholder'}
                               selectedId={config?.dayOfWeek} />
        </SpaceBetweenRow>

        <SpaceBetweenRow>
          <BodyRegular style={{whiteSpace: 'nowrap', marginRight: 16}}
                       weight={600}>{t('settings.whereabouts-reminder-config.hour')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={isLoading}
                               onSelect={onHourToSendSelected}
                               items={HOUR_TO_SEND_REMINDER}
                               placeholder={'settings.whereabouts-reminder-config.hour-placeholder'}
                               selectedId={config?.hourOfDay} />
        </SpaceBetweenRow>

        <SpaceBetweenRow>
          <BodyRegular style={{whiteSpace: 'nowrap', marginRight: 16}}
                       weight={600}>{t('settings.whereabouts-reminder-config.timezone')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={isLoading}
                               onSelect={onTimezoneSelected}
                               items={timezones}
                               placeholder={'settings.whereabouts-reminder-config.timezone-placeholder'}
                               selectedId={config?.timezone ?? undefined} />
        </SpaceBetweenRow>
      </>}
    </ConfigurationSection>
  )
});

WhereaboutsReminderSetting.displayName = 'WhereaboutsReminderSetting';

export interface WhereaboutsReminderSettingHandle {
  save: () => Promise<void>;
}

interface Props {
  deferSave?: boolean;
}

const SpaceBetweenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  .dropdown {
    margin-bottom: 0;
    max-width: 200px;
  }
`
